import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FiHome,
  FiMusic,
  FiZap,
  FiChevronLeft,
  FiChevronRight,
  FiCreditCard,
  FiInbox,
  FiCalendar,
  FiUsers,
  FiAward,
  FiGlobe,
  FiLayout,
  FiBook,
  FiMessageCircle
} from 'react-icons/fi';
import { useSidebar } from '../contexts/SidebarContext';
import { useAdminMockCreditTicketCounts } from '../hooks/useAdminMockCreditTicketCounts';
import { useAdminSubscriptionPurchaseTicketCounts } from '../hooks/useAdminSubscriptionPurchaseTicketCounts';
import AdminSidebarBadge from './AdminSidebarBadge';
import NaatiLogo from './NaatiLogo';
import './Sidebar.css';

const AdminSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen } = useSidebar();
  const { stats: mockTicketStats } = useAdminMockCreditTicketCounts(true);
  const { stats: subTicketStats } = useAdminSubscriptionPurchaseTicketCounts(true);

  const mockOpenCount = mockTicketStats.open_total;
  const mockUrgentCount = mockTicketStats.awaiting_admin;
  const subOpenCount = subTicketStats.open_total;
  const subUrgentCount = subTicketStats.awaiting_admin;

  const mockCreditsTitle =
    mockOpenCount > 0
      ? `Mock Credits — ${mockOpenCount} open ticket${mockOpenCount === 1 ? '' : 's'}${
          mockUrgentCount > 0 ? ` (${mockUrgentCount} need review)` : ''
        }`
      : 'Mock Credits';

  const subPurchaseTitle =
    subOpenCount > 0
      ? `Subscription Requests — ${subOpenCount} open ticket${subOpenCount === 1 ? '' : 's'}${
          subUrgentCount > 0 ? ` (${subUrgentCount} need review)` : ''
        }`
      : 'Subscription Requests';

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && window.innerWidth <= 768 && (
        <div 
          className="sidebar-overlay"
          onClick={() => setIsMobileOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}
      <motion.div
        className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}
        initial={{ x: -300 }}
        animate={{ x: 0 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      >
      <div className="sidebar-header">
        <motion.div
          className="sidebar-logo"
          whileHover={{ scale: 1.05 }}
          onClick={() => navigate('/admin/dashboard')}
        >
          <NaatiLogo
            className="sidebar-logo-wrap"
            imgClassName="sidebar-logo-img"
            responsive
          />
          {!isCollapsed && (
            <span className="logo-subtitle">Admin Panel</span>
          )}
        </motion.div>
        <button
          className="sidebar-toggle"
          onClick={() => {
            if (window.innerWidth <= 768) {
              setIsMobileOpen(false);
            } else {
              setIsCollapsed(!isCollapsed);
            }
          }}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {React.createElement(
            (isCollapsed ? FiChevronRight : FiChevronLeft) as React.ComponentType<{ size?: number }>,
            { size: 20 }
          )}
        </button>
      </div>

      <nav className="sidebar-nav">
        {!isCollapsed && (
          <>
            <div className="menu-item-wrapper">
              <motion.div
                className={`menu-item ${isActive('/admin/dashboard') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admin/dashboard');
                  if (window.innerWidth <= 768) {
                    setIsMobileOpen(false);
                  }
                }}
                whileHover={{ backgroundColor: 'rgba(230, 57, 70, 0.1)' }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="menu-item-content">
                  <span className="menu-icon">
                    {React.createElement(FiHome as React.ComponentType<{ size?: number }>, { size: 18 })}
                  </span>
                  <span className="menu-title">Dashboard</span>
                </div>
              </motion.div>
            </div>
            <div className="menu-item-wrapper">
              <motion.div
                className={`menu-item ${isActive('/admin/landing-page') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admin/landing-page');
                  if (window.innerWidth <= 768) {
                    setIsMobileOpen(false);
                  }
                }}
                whileHover={{ backgroundColor: 'rgba(230, 57, 70, 0.1)' }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="menu-item-content">
                  <span className="menu-icon">
                    {React.createElement(FiLayout as React.ComponentType<{ size?: number }>, { size: 18 })}
                  </span>
                  <span className="menu-title">Landing page</span>
                </div>
              </motion.div>
            </div>
            <div className="menu-item-wrapper">
              <motion.div
                className={`menu-item ${isActive('/admin/materials') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admin/materials');
                  if (window.innerWidth <= 768) {
                    setIsMobileOpen(false);
                  }
                }}
                whileHover={{ backgroundColor: 'rgba(230, 57, 70, 0.1)' }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="menu-item-content">
                  <span className="menu-icon">
                    {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 18 })}
                  </span>
                  <span className="menu-title">Material Management</span>
                </div>
              </motion.div>
            </div>
            <div className="menu-item-wrapper">
              <motion.div
                className={`menu-item menu-item--has-badge ${location.pathname.startsWith('/admin/mock-credits') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admin/mock-credits');
                  if (window.innerWidth <= 768) {
                    setIsMobileOpen(false);
                  }
                }}
                whileHover={{ backgroundColor: 'rgba(230, 57, 70, 0.1)' }}
                whileTap={{ scale: 0.98 }}
                title={mockCreditsTitle}
              >
                <div className="menu-item-content">
                  <span className="menu-icon">
                    {React.createElement(FiInbox as React.ComponentType<{ size?: number }>, { size: 18 })}
                  </span>
                  <span className="menu-title">Mock Credits</span>
                </div>
                <AdminSidebarBadge count={mockOpenCount} urgentCount={mockUrgentCount} />
              </motion.div>
            </div>
            <div className="menu-item-wrapper">
              <motion.div
                className={`menu-item menu-item--has-badge ${location.pathname.startsWith('/admin/subscription-requests') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admin/subscription-requests');
                  if (window.innerWidth <= 768) {
                    setIsMobileOpen(false);
                  }
                }}
                whileHover={{ backgroundColor: 'rgba(230, 57, 70, 0.1)' }}
                whileTap={{ scale: 0.98 }}
                title={subPurchaseTitle}
              >
                <div className="menu-item-content">
                  <span className="menu-icon">
                    {React.createElement(FiCreditCard as React.ComponentType<{ size?: number }>, { size: 18 })}
                  </span>
                  <span className="menu-title">Subscription Requests</span>
                </div>
                <AdminSidebarBadge count={subOpenCount} urgentCount={subUrgentCount} />
              </motion.div>
            </div>
            <div className="menu-item-wrapper">
              <motion.div
                className={`menu-item ${location.pathname.startsWith('/admin/naati-ai') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admin/naati-ai');
                  if (window.innerWidth <= 768) {
                    setIsMobileOpen(false);
                  }
                }}
                whileHover={{ backgroundColor: 'rgba(230, 57, 70, 0.1)' }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="menu-item-content">
                  <span className="menu-icon">
                    {React.createElement(FiMessageCircle as React.ComponentType<{ size?: number }>, { size: 18 })}
                  </span>
                  <span className="menu-title">NAATI AI</span>
                </div>
              </motion.div>
            </div>
            <div className="menu-item-wrapper">
              <motion.div
                className={`menu-item ${location.pathname.startsWith('/admin/mock-grading') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admin/mock-grading');
                  if (window.innerWidth <= 768) {
                    setIsMobileOpen(false);
                  }
                }}
                whileHover={{ backgroundColor: 'rgba(230, 57, 70, 0.1)' }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="menu-item-content">
                  <span className="menu-icon">
                    {React.createElement(FiAward as React.ComponentType<{ size?: number }>, { size: 18 })}
                  </span>
                  <span className="menu-title">Mock Grading</span>
                </div>
              </motion.div>
            </div>
            <div className="menu-item-wrapper">
              <motion.div
                className={`menu-item ${isActive('/admin/lab') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admin/lab');
                  if (window.innerWidth <= 768) {
                    setIsMobileOpen(false);
                  }
                }}
                whileHover={{ backgroundColor: 'rgba(230, 57, 70, 0.1)' }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="menu-item-content">
                  <span className="menu-icon">
                    {React.createElement(FiZap as React.ComponentType<{ size?: number }>, { size: 18 })}
                  </span>
                  <span className="menu-title">Lab</span>
                </div>
              </motion.div>
            </div>
            <div className="menu-item-wrapper">
              <motion.div
                className={`menu-item ${isActive('/admin/subscriptions') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admin/subscriptions');
                  if (window.innerWidth <= 768) {
                    setIsMobileOpen(false);
                  }
                }}
                whileHover={{ backgroundColor: 'rgba(230, 57, 70, 0.1)' }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="menu-item-content">
                  <span className="menu-icon">
                    {React.createElement(FiCreditCard as React.ComponentType<{ size?: number }>, { size: 18 })}
                  </span>
                  <span className="menu-title">Subscriptions</span>
                </div>
              </motion.div>
            </div>
            <div className="menu-item-wrapper">
              <motion.div
                className={`menu-item ${isActive('/admin/public-access') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admin/public-access');
                  if (window.innerWidth <= 768) {
                    setIsMobileOpen(false);
                  }
                }}
                whileHover={{ backgroundColor: 'rgba(230, 57, 70, 0.1)' }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="menu-item-content">
                  <span className="menu-icon">
                    {React.createElement(FiGlobe as React.ComponentType<{ size?: number }>, { size: 18 })}
                  </span>
                  <span className="menu-title">Public access</span>
                </div>
              </motion.div>
            </div>
            <div className="menu-item-wrapper">
              <motion.div
                className={`menu-item ${isActive('/admin/booking') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admin/booking');
                  if (window.innerWidth <= 768) {
                    setIsMobileOpen(false);
                  }
                }}
                whileHover={{ backgroundColor: 'rgba(230, 57, 70, 0.1)' }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="menu-item-content">
                  <span className="menu-icon">
                    {React.createElement(FiCalendar as React.ComponentType<{ size?: number }>, { size: 18 })}
                  </span>
                  <span className="menu-title">Booking Management</span>
                </div>
              </motion.div>
            </div>
            <div className="menu-item-wrapper">
              <motion.div
                className={`menu-item ${isActive('/admin/courses') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admin/courses');
                  if (window.innerWidth <= 768) {
                    setIsMobileOpen(false);
                  }
                }}
                whileHover={{ backgroundColor: 'rgba(230, 57, 70, 0.1)' }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="menu-item-content">
                  <span className="menu-icon">
                    {React.createElement(FiBook as React.ComponentType<{ size?: number }>, { size: 18 })}
                  </span>
                  <span className="menu-title">Courses Management</span>
                </div>
              </motion.div>
            </div>
            <div className="menu-item-wrapper">
              <motion.div
                className={`menu-item ${isActive('/admin/users') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admin/users');
                  if (window.innerWidth <= 768) {
                    setIsMobileOpen(false);
                  }
                }}
                whileHover={{ backgroundColor: 'rgba(230, 57, 70, 0.1)' }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="menu-item-content">
                  <span className="menu-icon">
                    {React.createElement(FiUsers as React.ComponentType<{ size?: number }>, { size: 18 })}
                  </span>
                  <span className="menu-title">User Management</span>
                </div>
              </motion.div>
            </div>
          </>
        )}
        {isCollapsed && (
          <div className="collapsed-menu">
            <div
              className="collapsed-menu-item"
              onClick={() => {
                navigate('/admin/dashboard');
                if (window.innerWidth <= 768) {
                  setIsMobileOpen(false);
                }
              }}
              title="Dashboard"
            >
              {React.createElement(
                FiHome as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
            </div>
            <div
              className="collapsed-menu-item"
              onClick={() => {
                navigate('/admin/landing-page');
                if (window.innerWidth <= 768) {
                  setIsMobileOpen(false);
                }
              }}
              title="Landing page"
            >
              {React.createElement(
                FiLayout as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
            </div>
            <div
              className="collapsed-menu-item"
              onClick={() => {
                navigate('/admin/materials');
                if (window.innerWidth <= 768) {
                  setIsMobileOpen(false);
                }
              }}
              title="Material Management"
            >
              {React.createElement(
                FiMusic as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
            </div>
            <div
              className="collapsed-menu-item collapsed-menu-item--with-badge"
              onClick={() => {
                navigate('/admin/mock-credits');
                if (window.innerWidth <= 768) {
                  setIsMobileOpen(false);
                }
              }}
              title={mockCreditsTitle}
            >
              {React.createElement(
                FiInbox as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
              <AdminSidebarBadge
                count={mockOpenCount}
                urgentCount={mockUrgentCount}
                collapsed
              />
            </div>
            <div
              className="collapsed-menu-item collapsed-menu-item--with-badge"
              onClick={() => {
                navigate('/admin/subscription-requests');
                if (window.innerWidth <= 768) {
                  setIsMobileOpen(false);
                }
              }}
              title={subPurchaseTitle}
            >
              {React.createElement(
                FiCreditCard as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
              <AdminSidebarBadge
                count={subOpenCount}
                urgentCount={subUrgentCount}
                collapsed
              />
            </div>
            <div
              className="collapsed-menu-item"
              onClick={() => {
                navigate('/admin/naati-ai');
                if (window.innerWidth <= 768) {
                  setIsMobileOpen(false);
                }
              }}
              title="NAATI AI"
            >
              {React.createElement(
                FiMessageCircle as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
            </div>
            <div
              className="collapsed-menu-item"
              onClick={() => {
                navigate('/admin/mock-grading');
                if (window.innerWidth <= 768) {
                  setIsMobileOpen(false);
                }
              }}
              title="Mock Grading"
            >
              {React.createElement(
                FiAward as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
            </div>
            <div
              className="collapsed-menu-item"
              onClick={() => {
                navigate('/admin/lab');
                if (window.innerWidth <= 768) {
                  setIsMobileOpen(false);
                }
              }}
              title="Lab"
            >
              {React.createElement(
                FiZap as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
            </div>
            <div
              className="collapsed-menu-item"
              onClick={() => {
                navigate('/admin/subscriptions');
                if (window.innerWidth <= 768) {
                  setIsMobileOpen(false);
                }
              }}
              title="Subscriptions"
            >
              {React.createElement(
                FiCreditCard as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
            </div>
            <div
              className="collapsed-menu-item"
              onClick={() => {
                navigate('/admin/public-access');
                if (window.innerWidth <= 768) {
                  setIsMobileOpen(false);
                }
              }}
              title="Public access"
            >
              {React.createElement(
                FiGlobe as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
            </div>
            <div
              className="collapsed-menu-item"
              onClick={() => {
                navigate('/admin/booking');
                if (window.innerWidth <= 768) {
                  setIsMobileOpen(false);
                }
              }}
              title="Booking Management"
            >
              {React.createElement(
                FiCalendar as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
            </div>
            <div
              className="collapsed-menu-item"
              onClick={() => {
                navigate('/admin/courses');
                if (window.innerWidth <= 768) {
                  setIsMobileOpen(false);
                }
              }}
              title="Courses Management"
            >
              {React.createElement(
                FiBook as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
            </div>
            <div
              className="collapsed-menu-item"
              onClick={() => {
                navigate('/admin/users');
                if (window.innerWidth <= 768) {
                  setIsMobileOpen(false);
                }
              }}
              title="User Management"
            >
              {React.createElement(
                FiUsers as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
            </div>
          </div>
        )}
      </nav>

      <div className="sidebar-footer">
        {!isCollapsed && (
          <div className="footer-info">
            <p className="footer-text">NAATI Admin Panel</p>
            <p className="footer-copyright">© 2025 All rights reserved</p>
          </div>
        )}
      </div>
    </motion.div>
    </>
  );
};

export default AdminSidebar;

